import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { serviceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { AlertTriangle, Gauge, ChevronRight, X } from 'lucide-react';
import { Button } from '../components/ui/button';

const KMCaptureBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const envPrefix = location.pathname.startsWith('/test') ? '/test' : '';
  const { user, isSupervisor, isAdmin } = useAuth();
  const [kmStatus, setKmStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [minimized, setMinimized] = useState(false);

  const fetchKMStatus = useCallback(async () => {
    if (!isSupervisor && !isAdmin) return;
    
    try {
      const response = await serviceAPI.getKMCaptureStatus();
      setKmStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch KM status:', error);
    } finally {
      setLoading(false);
    }
  }, [isSupervisor, isAdmin]);

  useEffect(() => {
    fetchKMStatus();
    // Refresh status every 5 minutes
    const interval = setInterval(fetchKMStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchKMStatus]);

  // Don't show for non-supervisors or if all captured
  if (!isSupervisor && !isAdmin) return null;
  if (loading) return null;
  if (!kmStatus || kmStatus.all_captured) return null;

  const pendingCount = kmStatus.vehicles_pending;

  if (minimized) {
    return (
      <div 
        className="fixed bottom-4 right-4 z-50 bg-amber-500 text-white px-4 py-2 rounded-lg shadow-lg cursor-pointer hover:bg-amber-600 transition-colors flex items-center gap-2"
        onClick={() => setMinimized(false)}
        data-testid="km-banner-minimized"
      >
        <AlertTriangle className="w-5 h-5" />
        <span className="font-semibold">{pendingCount} KM pending</span>
      </div>
    );
  }

  return (
    <div 
      className="bg-amber-50 border-b-2 border-amber-400 px-4 py-3"
      data-testid="km-capture-banner"
    >
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-amber-500 p-2 rounded-lg">
            <Gauge className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-semibold text-amber-800">
              Daily KM Capture Required
            </p>
            <p className="text-sm text-amber-700">
              {pendingCount} of {kmStatus.total_vehicles} vehicles need odometer readings today
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button 
            onClick={() => navigate(`${envPrefix}/service-schedule`)}
            className="bg-amber-600 hover:bg-amber-700 text-white flex-1 sm:flex-none"
            data-testid="km-capture-btn"
          >
            Record KM Now
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setMinimized(true)}
            className="text-amber-700 hover:bg-amber-100"
            title="Minimize (will appear at bottom right)"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Show pending vehicles */}
      {kmStatus.pending_vehicles?.length > 0 && (
        <div className="max-w-7xl mx-auto mt-2 flex flex-wrap gap-2">
          <span className="text-xs text-amber-700 font-medium">Pending:</span>
          {kmStatus.pending_vehicles.slice(0, 5).map(v => (
            <span key={v.id} className="text-xs bg-amber-200 px-2 py-0.5 rounded font-mono">
              {v.registration}
            </span>
          ))}
          {kmStatus.pending_vehicles.length > 5 && (
            <span className="text-xs text-amber-600">
              +{kmStatus.pending_vehicles.length - 5} more
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default KMCaptureBanner;
